import type { Root, Element, Text } from "hast";

/**
 * Rehype 插件：移除/折叠文本节点中的空白与换行，解决渲染后 HTML 带换行导致的空隙
 * 需在 rehypeRaw 之前运行，处理 remark-rehype 产出的标准 HAST
 *
 * 处理逻辑：
 * 1. 对文本节点：首尾 trim，内部连续空白（含换行）折叠为一个空格
 * 2. 移除仅含空白的文本节点
 * 3. 跳过 pre/code 内部，保留代码块格式
 */
export function rehypeRemoveWhitespace() {
  return (tree: Root) => {
    if (!tree?.children) return;

    function collapseTextNode(text: Text, insidePreOrCode: boolean): void {
      if (insidePreOrCode) return;
      const raw = (text.value ?? "").trim();
      text.value = raw.length > 0 ? raw.replace(/\s+/g, " ") : "";
    }

    function removeWhitespaceTextNodes(children: any[]): any[] {
      return children.filter((child) => {
        if (child?.type === "text") {
          return ((child as Text).value ?? "").trim().length > 0;
        }
        return true;
      });
    }

    function traverse(node: any, insidePreOrCode = false) {
      if (!node || typeof node !== "object") return;

      if (node.type === "text") {
        collapseTextNode(node as Text, insidePreOrCode);
        return;
      }

      if (node.type === "element") {
        const el = node as Element;
        const tag = el.tagName?.toLowerCase();
        const skip = tag === "pre" || tag === "code";
        const nextInside = insidePreOrCode || skip;

        if (el.children) {
          el.children.forEach((c) => traverse(c, nextInside));
          el.children = removeWhitespaceTextNodes(el.children);
        }
      }
    }

    if (tree.children) {
      tree.children.forEach((c) => traverse(c));
      tree.children = removeWhitespaceTextNodes(tree.children);
    }
  };
}
